import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Subscription, timeout } from 'rxjs';
import { AuthRepository } from 'app/core/auth/auth.repository';
import { AppConfigService } from 'app/core/config/app-config.service';
import { MediaDeviceCoordinatorService } from './media-device-coordinator.service';
import { Router, NavigationStart, NavigationCancel, NavigationError, NavigationEnd } from '@angular/router';

interface CaptureSession {
  id: string; step: number; owner: number; base: string; mimeType: string;
  source: 'ai' | 'input'; recordedAt: string; durationMs: number;
  chunkCount: number; ack: number; closed: boolean; interrupted: boolean;
  tab: string; heartbeat: number; retryAt: number; failures: number;
}
interface CaptureChunk { key: string; session: string; sequence: number; blob: Blob; sha: string; capturedFrom?: number; capturedTo?: number; }
interface ActiveCapture {
  session: CaptureSession; recorder: MediaRecorder; stream: MediaStream;
  writes: Promise<void>; stopped: Promise<void>; finish: () => void; started: number;
  lastDataAt: number;
}

/** One student microphone owner; durable delivery is independent of recognition and grading. */
@Injectable({ providedIn: 'root' })
export class StepAudioCaptureService implements OnDestroy {
  readonly state$ = new BehaviorSubject<{ status: string; pending: number; error?: string }>({ status: 'saved', pending: 0 });
  private step: number | null = null;
  private observedOwner = 0;
  private sources = new Map<string, MediaStream>();
  private active: ActiveCapture | null = null;
  private captures = new Map<string, ActiveCapture>();
  private busy = false;
  private sharedStream: MediaStream | null = null;
  private tab = crypto.randomUUID();
  private db: Promise<IDBDatabase>;
  contextRevision = 0;
  get currentStepId(): number | null { return this.step; }
  /**
   * Retention is switched on for this course. Pushed in by GlobalAiChatService
   * rather than read from it, because that service already depends on this one.
   *
   * Starts false and returns to false on academy exit: the switch has to fail
   * closed, or a settings read that never arrives would record the student.
   */
  private recordingEnabled = false;
  /** Steps the server has refused. A live 403 must not start the next capture. */
  private refusedSteps = new Set<number>();
  /** Sessions already thrown away, so a late recorder stop cannot upload them again. */
  private discardedSessions = new Set<string>();
  private suspended = false;
  private subscriptions = new Subscription();
  private listeners: Array<() => void> = [];
  private interval: ReturnType<typeof setInterval>;
  private destroyed = false;
  private deliveryWaiters: Array<() => void> = [];

  constructor(private http: HttpClient, private auth: AuthRepository, private config: AppConfigService, hub: MediaDeviceCoordinatorService, router: Router) {
    this.observedOwner = this.owner();
    this.db = new Promise((resolve, reject) => {
      const open = indexedDB.open('studema-step-audio', 1);
      open.onupgradeneeded = () => {
        open.result.createObjectStore('sessions', { keyPath: 'id' });
        open.result.createObjectStore('chunks', { keyPath: 'key' });
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error);
    });
    void this.db.catch(e => this.failed(e));
    let departing: { id: number; step: number | null; owner: number } | null = null;
    this.subscriptions.add(router.events.subscribe(event => {
      if (event instanceof NavigationStart && event.url.split(/[?#]/)[0] !== (router.url ?? '').split(/[?#]/)[0]) {
        departing = { id: event.id, step: this.step ?? departing?.step ?? null, owner: this.owner() };
        this.contextRevision++; this.bindStep(null);
      }
      if ((event instanceof NavigationCancel || event instanceof NavigationError) && departing?.id === event.id) {
        const previous = departing; departing = null;
        if (!this.step && previous.owner === this.owner()) this.bindStep(previous.step);
      }
      if (event instanceof NavigationEnd && departing?.id === event.id) departing = null;
    }));
    this.subscriptions.add(this.auth.$user?.subscribe(() => { this.syncOwner(); void this.deliver(); }));
    this.subscriptions.add(hub.getMediaState().subscribe(state => { this.sharedStream = state.microphoneStream; this.reconcile(); }));
    // The widget publishes its actual microphone, never the generated tutor output.
    this.listen(window, 'ai-chat-microphone', ((event: CustomEvent) => {
      this.attach('ai', event.detail?.stream ?? null);
    }) as EventListener);
    this.listen(window, 'ai-chat-step-audio', ((event: CustomEvent) => {
      const detail = event.detail;
      if (detail?.blob instanceof Blob) void this.preserveBlob(Number(detail.studentCourseStepId), detail.blob, { source: 'ai', durationMs: detail.durationMs }).catch(e => this.failed(e));
    }) as EventListener);
    this.listen(window, 'online', () => void this.deliver());
    this.listen(document, 'visibilitychange', () => {
      if (document.hidden) this.flush(); else this.reconcile();
    });
    this.listen(window, 'pagehide', () => { this.suspended = true; this.close(true); });
    this.listen(window, 'pageshow', () => { this.suspended = false; this.reconcile(); });
    this.interval = setInterval(() => { this.reconcile(); void this.deliver(); }, 5000);
  }

  private listen(target: EventTarget, name: string, handler: EventListener): void {
    target.addEventListener(name, handler);
    this.listeners.push(() => target.removeEventListener(name, handler));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.suspended = true;
    clearInterval(this.interval);
    this.subscriptions.unsubscribe();
    for (const remove of this.listeners) remove();
    this.close(true);
    void Promise.all([...this.captures.values()].map(c => c.stopped))
      .then(() => this.busy ? new Promise<void>(resolve => this.deliveryWaiters.push(resolve)) : undefined)
      .then(() => this.db).then(db => db.close());
  }

  async detach(source: 'ai' | 'input'): Promise<void> {
    const before = this.active;
    this.attach(source, null);
    if (before && before !== this.active) await before.stopped;
  }

  async finalizeBeforeReload(): Promise<void> {
    this.suspended = true;
    this.close(false);
    await Promise.all([...this.captures.values()].map(async c => { await c.stopped; await c.writes; }));
  }

  bindStep(step: number | null): void {
    this.syncOwner();
    const next = Number(step) > 0 ? Number(step) : null;
    if (next === this.step) return;
    this.close(false);
    this.step = next;
    this.reconcile();
  }

  async preserveBlob(step: number, blob: Blob, opts: { source: 'ai' | 'input'; durationMs?: number }): Promise<void> {
    const owner = this.owner();
    if (!owner || !step || !blob.size) throw new Error('Recording has no student/step identity');
    const session: CaptureSession = { id: crypto.randomUUID(), step, owner,
      base: this.config.get<string>('api_baseurl', '').replace(/\/$/, ''),
      mimeType: blob.type || 'audio/webm', source: opts.source, recordedAt: new Date().toISOString(),
      durationMs: opts.durationMs ?? 0, chunkCount: 0, ack: 0, closed: false, interrupted: false,
      tab: this.tab, heartbeat: Date.now(), retryAt: 0, failures: 0 };
    for (let offset = 0; offset < blob.size; offset += 4 * 1024 * 1024) {
      const part = blob.slice(offset, offset + 4 * 1024 * 1024);
      const digest = await crypto.subtle.digest('SHA-256', await part.arrayBuffer());
      const sha = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
      const sequence = session.chunkCount++;
      await this.persistChunk(session, { key: session.id + ':' + sequence, session: session.id, sequence, blob: part, sha });
    }
    session.closed = true;
    await this.put('sessions', session);
    void this.deliver();
  }

  attach(source: 'ai' | 'input', stream: MediaStream | null): void {
    if (stream) this.sources.set(source, stream); else this.sources.delete(source);
    this.reconcile();
  }

  private owner(): number {
    const user = this.auth.getUser();
    return Number(user?.role) === 5 ? Number(user.person_id) : 0;
  }

  private syncOwner(): void {
    const owner = this.owner();
    if (owner === this.observedOwner) return;
    this.observedOwner = owner;
    this.refusedSteps.clear();
    this.contextRevision++;
    this.close(true);
    this.step = null;
    this.sources.clear();
  }

  /**
   * The single switch for keeping audio at all.
   *
   * Every source funnels through reconcile(), so gating here covers the AI
   * tutor and plain dictation alike — "turn recording off" has to mean off,
   * not off for one of the two ways a microphone gets opened.
   */
  setRecordingEnabled(enabled: boolean): void {
    if (this.recordingEnabled === enabled) return;
    this.recordingEnabled = enabled;
    // Anything already running was authorised under the old answer; stop it
    // and keep what was captured rather than discarding the student's work.
    if (!enabled) this.close(false);
    else this.reconcile();
  }

  /**
   * Forget refusals from the course just left.
   *
   * The next course, or the next student on this browser, gets a clean try.
   * A refusal only means "this step, this login", not "never record again".
   */
  clearRefusedSteps(): void {
    this.refusedSteps.clear();
  }

  private reconcile(): void {
    this.syncOwner();
    if (this.suspended) return;
    if (!this.recordingEnabled) {
      if (this.active) this.close(false);
      return;
    }
    if (this.step != null && this.refusedSteps.has(this.step)) {
      if (this.active) this.close(false);
      return;
    }
    const owner = this.owner();
    const entry = [...this.sources.entries()].find(([, s]) => s.getAudioTracks().some(t => t.readyState === 'live' && t.enabled));
    const stream = entry && this.sharedStream?.getAudioTracks().some(t => t.readyState === 'live') ? this.sharedStream : entry?.[1];
    const track = stream?.getAudioTracks()[0];
    if (this.active && (this.active.session.owner !== owner || !track || this.active.stream.getAudioTracks()[0] !== track)) this.close(false);
    if (this.active || !owner || !this.step || !entry || typeof MediaRecorder === 'undefined') return;
    try {
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(stream!, mimeType ? { mimeType } : undefined);
      const session: CaptureSession = {
        id: crypto.randomUUID(), step: this.step, owner, base: this.config.get<string>('api_baseurl', '').replace(/\/$/, ''),
        mimeType: recorder.mimeType || mimeType || 'audio/webm', source: entry[0] as 'ai' | 'input',
        recordedAt: new Date().toISOString(), durationMs: 0, chunkCount: 0, ack: 0,
        closed: false, interrupted: false, tab: this.tab, heartbeat: Date.now(), retryAt: 0, failures: 0,
      };
      let finish!: () => void;
      const stopped = new Promise<void>(resolve => finish = resolve);
      const capture: ActiveCapture = { session, recorder, stream: stream!, writes: this.put('sessions', session), stopped, finish, started: Date.now(), lastDataAt: Date.now() };
      this.active = capture;
      this.captures.set(session.id, capture);
      recorder.ondataavailable = event => {
        if (!event.data.size) return;
        // Observe actual callback times before asynchronous hashing/storage work.
        // Split parts of the same browser event share its interval; no invented seconds.
        const capturedFrom = capture.lastDataAt;
        const capturedTo = Math.max(capturedFrom, Date.now());
        capture.lastDataAt = capturedTo;
        capture.writes = capture.writes.then(async () => {
          // Delayed mobile/background events may be much larger than a second.
          for (let offset = 0; offset < event.data.size; offset += 4 * 1024 * 1024) {
            const blob = event.data.slice(offset, offset + 4 * 1024 * 1024);
            const sequence = session.chunkCount;
            const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
            const sha = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
            session.chunkCount++;
            session.durationMs = capturedTo - capture.started;
            session.heartbeat = Date.now();
            await this.persistChunk(session, { key: session.id + ':' + sequence, session: session.id, sequence, blob, sha, capturedFrom, capturedTo });
          }
        });
        void capture.writes.catch(e => this.failed(e));
      };
      recorder.onstop = () => {
        if (this.discardedSessions.has(session.id)) {
          if (this.active === capture) this.active = null;
          this.captures.delete(session.id);
          finish();
          return;
        }
        const unexpected = this.active === capture;
        if (unexpected) { this.active = null; session.interrupted = true; }
        capture.writes = capture.writes.then(async () => {
          session.closed = true;
          session.durationMs = Date.now() - capture.started;
          await this.put('sessions', session);
        });
        void capture.writes.then(() => { this.captures.delete(session.id); void this.deliver(); }).catch(e => this.failed(e)).finally(finish);
        if (unexpected) this.reconcile();
      };
      recorder.onerror = () => {
        session.interrupted = true;
        // A delayed error from an old recorder must not stop its replacement.
        if (this.active === capture) this.close(true);
      };
      track?.addEventListener('ended', () => this.reconcile(), { once: true });
      recorder.start(1000);
      this.state$.next({ status: 'recording', pending: this.state$.value.pending });
    } catch (e) { this.failed(e); }
  }

  private flush(): void {
    try { if (this.active?.recorder.state === 'recording') this.active.recorder.requestData(); } catch (e) { this.failed(e); }
  }
  private close(interrupted: boolean): void {
    const capture = this.active;
    this.active = null;
    if (!capture) return;
    capture.session.interrupted ||= interrupted;
    try { if (capture.recorder.state !== 'inactive') capture.recorder.stop(); } catch (e) { this.failed(e); }
  }
  private failed(error: unknown): void {
    console.error('Student recording preservation failed', error);
    this.state$.next({ status: 'failed', pending: this.state$.value.pending, error: 'Audio could not be saved. Keep this page open and retry.' });
  }

  private async transaction(stores: string[], write: (tx: IDBTransaction) => void): Promise<void> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(stores, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = tx.onabort = () => reject(tx.error);
      write(tx);
    });
  }
  private put(store: string, value: unknown): Promise<void> {
    return this.transaction([store], tx => { tx.objectStore(store).put(value); });
  }
  private persistChunk(session: CaptureSession, chunk: CaptureChunk): Promise<void> {
    return this.transaction(['sessions', 'chunks'], tx => {
      tx.objectStore('sessions').put(session);
      tx.objectStore('chunks').put(chunk);
    });
  }
  /** Drop a session and its bytes; the server will never accept them. */
  private async abandon(sessionId: string): Promise<void> {
    const keys = (await this.all<CaptureChunk>('chunks'))
      .filter(c => c.session === sessionId)
      .map(c => c.key);
    await this.transaction(['sessions', 'chunks'], tx => {
      tx.objectStore('sessions').delete(sessionId);
      for (const key of keys) tx.objectStore('chunks').delete(key);
    });
  }

  private async all<T>(store: string): Promise<T[]> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const req = db.transaction(store).objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /** Only the authenticated owner may deliver this browser's pending audio. */
  async deliver(): Promise<void> {
    if (this.destroyed || this.busy || !navigator.onLine || !this.owner()) return;
    this.busy = true;
    try {
      const base = this.config.get<string>('api_baseurl', '').replace(/\/$/, '');
      const allSessions = await this.all<CaptureSession>('sessions');
      const sessions = allSessions.filter(s => s.owner === this.owner() && s.base === base);
      // Audio recorded against a different server is skipped by the filter
      // above on every pass, so it was never retried and never removed — it
      // just sat in IndexedDB counting against the browser's quota.
      for (const orphan of allSessions.filter(s => s.owner === this.owner() && s.base !== base && !this.captures.has(s.id))) {
        console.warn('Student recording discarded: recorded against a different server', { step: orphan.step, session: orphan.id, base: orphan.base });
        await this.abandon(orphan.id);
      }
      this.state$.next({ status: this.active ? 'recording' : sessions.length ? 'pending upload' : 'saved', pending: sessions.length });
      for (const stored of sessions) {
        if (stored.retryAt > Date.now()) continue;
        // Never rewrite a live capture's metadata using the upload snapshot.
        const local = this.captures.get(stored.id);
        const session = local?.session ?? stored;
        if (!session.closed && !local && Date.now() - session.heartbeat < 120000) continue;
        if (!session.closed && !local) { session.closed = true; session.interrupted = true; }
        if (!session.chunkCount) continue;
        const url = session.base + '/step-voice/' + session.step + '/sessions/' + session.id;
        const assertOwner = () => { if (session.owner !== this.owner()) throw new Error('Recording owner changed'); };
        try {
          assertOwner();
          const begun = await firstValueFrom(this.http.put<any>(url, session).pipe(timeout(30000)));
          if (begun?.data?.id !== session.id) throw new Error('Recording session was not acknowledged');
          const chunks = (await this.all<CaptureChunk>('chunks')).filter(c => c.session === session.id).sort((a, b) => a.sequence - b.sequence);
          for (const chunk of chunks) {
            assertOwner();
            const bytes = await chunk.blob.arrayBuffer();
            const digest = await crypto.subtle.digest('SHA-256', bytes);
            const sha = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
            const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream', 'X-Chunk-SHA256': sha };
            if (chunk.capturedFrom !== undefined && chunk.capturedTo !== undefined) {
              headers['X-Capture-From'] = String(chunk.capturedFrom);
              headers['X-Capture-To'] = String(chunk.capturedTo);
            }
            // sha256 is also on the query string: some proxies truncate custom headers.
            const ack = await firstValueFrom(this.http.put<any>(url + '/chunks/' + chunk.sequence + '?sha256=' + sha, bytes, { headers }).pipe(timeout(30000)));
            if (ack?.data?.id !== session.id || ack.data.sequence !== chunk.sequence || ack.data.sha256 !== sha) throw new Error('Audio bytes were not acknowledged');
            // Sequences are contiguous from zero and uploaded in order, so the
            // highest acknowledged one is how many chunks the server holds.
            // Recorded before the row is dropped: `chunkCount` counts slices
            // ever captured and cannot answer this after a partial delivery.
            session.ack = Math.max(session.ack, chunk.sequence + 1);
            // Acknowledged bytes can be removed independently of newer capture writes.
            await this.transaction(['chunks'], tx => { tx.objectStore('chunks').delete(chunk.key); });
            await this.put('sessions', session);
          }
          // A live capture can close *while* this pass is uploading, and
          // `session` is that same mutating object: `closed` flips to true and
          // the final slice lands after the loop already read the store. The
          // count then committed to was one ahead of what the server held,
          // which is the 422 ("Missing chunks") on /complete. Finish only what
          // is fully delivered; `onstop` calls deliver() again once its writes
          // have settled.
          const undelivered = (await this.all<CaptureChunk>('chunks')).filter(c => c.session === session.id).length;
          if (session.closed && !undelivered && session.ack > 0) {
            assertOwner();
            const response = await firstValueFrom(this.http.post<any>(url + '/complete', { ...session, chunkCount: session.ack }).pipe(timeout(120000)));
            if (response?.data?.id !== session.id || !['saved', 'partial'].includes(response?.data?.status)) throw new Error('Audio is awaiting server conversion');
            await this.transaction(['sessions'], tx => { tx.objectStore('sessions').delete(session.id); });
            window.dispatchEvent(new CustomEvent('step-recordings-saved', { detail: { studentStepId: session.step } }));
          }
        } catch (e: any) {
          // The step this audio belongs to is gone, or is not this student's
          // any more. Retrying cannot change that, including while the
          // recorder is still open: every new chunk would be another 403.
          if (e?.status === 403 || e?.status === 404) {
            console.warn('Student recording discarded: the step no longer accepts it', { step: session.step, session: session.id, status: e.status });
            this.refusedSteps.add(session.step);
            this.discardedSessions.add(session.id);
            const live = this.captures.get(session.id);
            if (live && this.active === live) this.close(false);
            if (live) {
              // stop() finishes on a later turn. Don't sit here forever if it never does.
              await Promise.race([
                live.stopped,
                new Promise<void>(resolve => setTimeout(resolve, 3000)),
              ]);
              await live.writes;
            }
            await this.abandon(session.id);
            continue;
          }
          session.failures++;
          const retryAfter = Number(e?.headers?.get?.('Retry-After')) * 1000;
          session.retryAt = Date.now() + (retryAfter || Math.min(300000, 1000 * 2 ** Math.min(session.failures, 8)));
          if (!this.captures.has(session.id)) await this.put('sessions', session);
          this.state$.next({ status: 'pending upload', pending: sessions.length, error: 'Audio is retained on this device; delivery will retry.' });
        }
      }
    } catch (e) { this.failed(e); } finally { this.busy = false; for (const resolve of this.deliveryWaiters.splice(0)) resolve(); }
  }
}

